import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/shell/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { SourcesPicker, assembleSourcesContext } from "@/components/content/SourcesPicker";
import { ModelPicker } from "@/components/content/ModelPicker";
import { RESEARCH_MODE_OPTIONS } from "@/lib/researchModes";
import { RecipePicker } from "@/components/content/RecipePicker";
import { normalizeRecipeDiscovery } from "@/lib/discoveryOptions";
import { resolveDefaultModel } from "@/lib/modelProviderSettings";
import { useAppState } from "@/state/AppStateProvider";
import { FileText, Plus, Sparkles, ArrowRight, ImageIcon, Clock, Eye, X as XIcon } from "lucide-react";
import { useDismissed } from "@/lib/useDismissed";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { timeAgo } from "@/lib/utils";

export default function BlogList() {
  const navigate = useNavigate();
  const { state, createBlog } = useAppState();
  const blogs = state.blogs || [];
  const [calloutsDismissed, dismissCallouts] = useDismissed("sce_blog_callouts_v1");

  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  // Brand recipe (brand + voice + one style); self-fills from the active brand.
  const [recipe, setRecipe] = useState({ brandId: null, voiceId: null, styleSkillId: "" });
  const [aiAudience, setAiAudience] = useState("");
  const [aiContext, setAiContext] = useState("");
  const [aiSources, setAiSources] = useState([]); // [{id, kind, title, url, text, words}]
  const [aiResearch, setAiResearch] = useState("none"); // none | sources_only | deep
  const [aiResearchProvider, setAiResearchProvider] = useState("free"); // free | ollama | browser
  const [aiResearchFocus, setAiResearchFocus] = useState("general"); // general | academic

  // Model provider + version for blog generation (defaults to the Settings default).
  const configuredProviders = useMemo(
    () => (state.modelSettings?.providers || []).filter((p) => p.configured),
    [state.modelSettings],
  );
  const [aiProviderId, setAiProviderId] = useState(() => resolveDefaultModel(state.modelSettings).providerId);
  const [aiModel, setAiModel] = useState(() => resolveDefaultModel(state.modelSettings).model);
  const aiProvider = useMemo(
    () => configuredProviders.find((p) => p.id === aiProviderId) || null,
    [configuredProviders, aiProviderId],
  );
  const onPickProvider = (id) => {
    setAiProviderId(id);
    const p = configuredProviders.find((x) => x.id === id);
    setAiModel(p?.defaultModel || "");
  };

  const onBlank = () => {
    const id = createBlog({ title: "Untitled blog post" });
    navigate(`/blog/${id}`);
  };

  const onAiGenerate = () => {
    setAiOpen(false);
    const id = createBlog({ title: aiTopic, body_html: "<p><em>Drafting with the agent…</em></p>" });
    toast.message("Agent generating first draft…");
    // Source text can be large (uploaded docs), so hand off via router state,
    // not URL params. The added sources are folded into the context here.
    const context = [aiContext?.trim() || "", assembleSourcesContext(aiSources)].filter(Boolean).join("\n\n");
    const recipeDiscovery = normalizeRecipeDiscovery(recipe);
    navigate(`/blog/${id}`, {
      state: {
        ai: true, topic: aiTopic, audience: aiAudience || "",
        context, research: aiResearch || "none",
        research_provider: aiResearchProvider || "ollama", research_focus: aiResearchFocus || "general",
        provider_id: aiProviderId || undefined, model: aiModel || undefined,
        brand_id: recipe.brandId || undefined, voice_id: recipe.voiceId || undefined, style_skill_id: recipe.styleSkillId || undefined,
        goal: recipe.goal || undefined,
        cta: typeof recipe.cta === "boolean" ? recipe.cta : undefined,
        cta_type: recipe.cta === true ? (recipe.ctaType || undefined) : undefined,
        seo: typeof recipe.seo === "boolean" ? recipe.seo : undefined,
        discovery_mode: recipeDiscovery.discoveryMode,
        hashtag_mode: recipeDiscovery.hashtagMode,
        destination_url: recipe.destinationUrl?.trim() || undefined,
        sources: aiSources.map((s) => ({ title: s.title, url: s.url, kind: s.kind })),
      },
    });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-7" data-testid="blog-list-page">
      <PageHeader
        eyebrow="Long-form"
        title="Blog Studio"
        description="Write long-form pieces in a dedicated workspace — generate first drafts with AI, then freely edit, drop images, and rearrange."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={onBlank} className="gap-1.5" data-testid="blog-new-blank"><Plus className="h-4 w-4" /> Start blank</Button>
            <Button onClick={() => setAiOpen(true)} className="gap-1.5" data-testid="blog-new-ai" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
              <Sparkles className="h-4 w-4" /> Generate with AI
            </Button>
          </div>
        }
      />

      {/* Hero callouts — dismissible first-run explainer */}
      {!calloutsDismissed && (
        <div className="relative">
          <button type="button" onClick={dismissCallouts} aria-label="Dismiss" className="absolute right-2 top-2 z-10 rounded-md bg-[color:var(--surface-1)]/80 p-1 text-muted-foreground hover:bg-white/[0.08]" data-testid="blog-callouts-dismiss">
            <XIcon className="h-3.5 w-3.5" />
          </button>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <CalloutCard icon={Sparkles} title="Agent-drafted" body="Same research-aware agent that powers social, focused on long-form structure." accent="cyan" />
            <CalloutCard icon={ImageIcon} title="Drop images anywhere" body="Drag & drop, paste from clipboard, or use the toolbar — images rearrange inline." accent="amber" />
            <CalloutCard icon={Eye} title="Approval-first" body="Drafts stay in your hands. Publish to your CMS only when you say so." accent="blue" />
          </div>
        </div>
      )}

      {/* Blog drafts grid */}
      <section className="space-y-3">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Drafts ({blogs.length})</p>
        {blogs.length === 0 ? (
          <div className="rounded-2xl border border-dashed p-12 text-center" style={{ borderColor: "var(--border-subtle)" }}>
            <FileText className="mx-auto h-6 w-6 text-muted-foreground" />
            <p className="mt-3 text-sm text-1">No blog drafts yet</p>
            <p className="mt-1 text-xs text-muted-foreground">Click <span className="text-1">Generate with AI</span> or start blank to begin.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {blogs.map((b) => (
              <Link
                key={b.id}
                to={`/blog/${b.id}`}
                data-testid={`blog-card-${b.id}`}
                className="group block overflow-hidden rounded-2xl border bg-card transition-colors hover:border-[color:var(--accent-cyan)]/40"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <div
                  className="relative aspect-[16/9] w-full overflow-hidden"
                  style={{
                    background: b.cover_image
                      ? `url(${b.cover_image}) center/cover`
                      : "radial-gradient(420px 220px at 30% 30%, rgba(143, 224, 74,0.25), transparent 60%), radial-gradient(360px 240px at 75% 75%, rgba(18, 147, 79,0.30), transparent 60%), linear-gradient(160deg, #0a1224 0%, #142447 60%, #0a1224 100%)",
                  }}
                  aria-hidden
                >
                  <span className="absolute right-3 top-3"><StatusBadge status={b.status} /></span>
                  <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full border bg-black/35 px-2 py-1 text-[10.5px] uppercase tracking-[0.16em] text-white/85" style={{ borderColor: "rgba(255,255,255,0.18)" }}>
                    <FileText className="h-3 w-3" /> Blog
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="line-clamp-2 text-[15px] font-semibold tracking-[-0.005em] text-1 group-hover:text-[color:var(--accent-cyan)]">{b.title}</h3>
                  <p className="mt-2 line-clamp-2 text-[12.5px] text-muted-foreground">{b.excerpt || "No excerpt yet."}</p>
                  <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {timeAgo(b.updated_at)}</span>
                    <span className="font-mono">{b.word_count || 0} words</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* AI generate dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent data-testid="blog-ai-dialog" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-[color:var(--accent-cyan)]" /> Generate a first draft
            </DialogTitle>
            <DialogDescription className="text-[12.5px] text-muted-foreground">
              The agent will draft a full blog post and drop it into the editor — keep, rewrite, or move things around freely.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Topic</Label>
              <Textarea value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} rows={2} placeholder="What should this post be about?" data-testid="blog-ai-topic" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Brand, voice & style</Label>
                <Link to="/brands" className="text-[11px] font-medium text-[color:var(--accent-cyan)] hover:underline" data-testid="blog-ai-manage-brands">Manage brands</Link>
              </div>
              <RecipePicker value={recipe} onChange={setRecipe} variant="light" context="blog" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Audience</Label>
                <Input value={aiAudience} onChange={(e) => setAiAudience(e.target.value)} placeholder="e.g. HVAC business owners" data-testid="blog-ai-audience" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Model</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={aiProviderId} onValueChange={onPickProvider}>
                  <SelectTrigger className="sm:w-[200px]" data-testid="blog-ai-provider" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}><SelectValue placeholder="Select a provider" /></SelectTrigger>
                  <SelectContent>
                    {configuredProviders.length === 0
                      ? <SelectItem value="none" disabled>No providers configured</SelectItem>
                      : configuredProviders.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}{p.status === "error" ? " (last failed)" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                {aiProvider && <ModelPicker provider={aiProvider} value={aiModel} onChange={setAiModel} variant="light" />}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Sources <span className="lowercase tracking-normal text-muted-foreground/70">(optional — URLs or upload PDF / Word / text)</span></Label>
              <SourcesPicker sources={aiSources} onChange={setAiSources} variant="dark" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[auto]">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Research</Label>
                <Select value={aiResearch} onValueChange={setAiResearch}>
                  <SelectTrigger className="sm:w-[160px]" data-testid="blog-ai-research" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RESEARCH_MODE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {aiResearch !== "none" && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Focus</Label>
                  <Select value={aiResearchFocus} onValueChange={setAiResearchFocus}>
                    <SelectTrigger data-testid="blog-ai-research-focus" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General web</SelectItem>
                      <SelectItem value="academic">Academic papers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {aiResearchFocus === "general" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Via</Label>
                    <Select value={aiResearchProvider} onValueChange={setAiResearchProvider}>
                      <SelectTrigger data-testid="blog-ai-research-provider" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free web search (no key)</SelectItem>
                        <SelectItem value="ollama">Ollama Web Search (your key)</SelectItem>
                        <SelectItem value="browser">Browser — opens real pages (slower)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Source material / context <span className="lowercase tracking-normal text-muted-foreground/70">(strongly recommended for niche topics)</span></Label>
              <Textarea value={aiContext} onChange={(e) => setAiContext(e.target.value)} rows={4} placeholder="Paste what the model can't know: notes, transcripts, product facts, your own definitions (e.g. what OpenClaw and Hermes Agent actually are). The agent grounds the post on this and won't invent facts." data-testid="blog-ai-context" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAiOpen(false)} data-testid="blog-ai-cancel">Cancel</Button>
            <Button onClick={onAiGenerate} disabled={!aiTopic.trim()} className="gap-1.5" data-testid="blog-ai-confirm" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
              <Sparkles className="h-4 w-4" /> Generate <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

function CalloutCard({ icon: Icon, title, body, accent }) {
  const color = accent === "amber" ? "#F6B24A" : accent === "blue" ? "#66B6FF" : "#8FE04A";
  return (
    <div
      className={cn("relative overflow-hidden rounded-2xl border p-5 inner-highlight")}
      style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-lg border" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
        <Icon className="h-4 w-4" style={{ color }} />
      </span>
      <h4 className="mt-3 text-sm font-semibold text-1">{title}</h4>
      <p className="mt-1 text-[12.5px] leading-[1.6] text-muted-foreground">{body}</p>
    </div>
  );
}
